/**
 * EmptySection — honest placeholder for an Insights chart whose
 * backend data source does not yet exist.
 *
 * Use instead of rendering hardcoded mock numbers. Keeps the layout
 * stable and the audit story consistent: the user sees that the
 * section is planned, not a fake chart.
 */

import { Info } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface EmptySectionProps {
  /** Short reason. Defaults to a generic backend-gap message. */
  message?: string;
  /** Optional smaller caption shown below the message. */
  hint?: string;
  className?: string;
  /** Match the typical chart body height when used inside ChartCard. */
  minHeight?: string;
}

export function EmptySection({
  message = 'Not yet available in this build.',
  hint,
  className,
  minHeight = 'min-h-32',
}: EmptySectionProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-1.5 rounded-md border border-dashed text-center p-6',
        minHeight,
        className,
      )}
    >
      <Info className="h-4 w-4 text-muted-foreground" />
      <p className="text-xs font-medium text-muted-foreground">{message}</p>
      {hint && (
        <p className="text-[11px] text-muted-foreground/80 max-w-md leading-relaxed">
          {hint}
        </p>
      )}
    </div>
  );
}
